import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Link,
  Radio,
  RadioGroup,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  PendingActions as PendingIcon,
  CheckCircle as CheckIcon,
  Download as DownloadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../config/api';

interface Form {
  id: number;
  formNumber: string;
  title: string;
  status: 'draft' | 'in_progress' | 'submitted' | 'under_review' | 'approved';
  completionPercentage: number;
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
}

interface PatentResult {
  patentNumber: string;
  title: string;
  abstract: string;
  applicant: string;
  publicationDate: string;
  ipcCodes: string[];
  espacenetUrl: string;
}

interface NoveltyQuery {
  label: string;
  description: string;
  cqlQuery: string;
}

interface NoveltyCheckResult {
  cqlQuery: string;
  analysis: string;
  patents: PatentResult[];
}

// Helper function to format date to DD-MM-YYYY in IST
const formatDateIST = (dateStr: string): string => {
  const date = new Date(dateStr);
  const istDate = new Date(date.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const day = String(istDate.getDate()).padStart(2, '0');
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const year = istDate.getFullYear();
  return `${day}-${month}-${year}`;
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [creatingForm, setCreatingForm] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    draft: 0,
    inProgress: 0,
    submitted: 0,
  });

  // AI Assessment states
  const [privacyDialogOpen, setPrivacyDialogOpen] = useState(false);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiAssessment, setAiAssessment] = useState<string>('');

  // Novelty Check states
  const [noveltyPrivacyDialogOpen, setNoveltyPrivacyDialogOpen] = useState(false);
  const [noveltyResultsDialogOpen, setNoveltyResultsDialogOpen] = useState(false);
  const [noveltySelectedFormId, setNoveltySelectedFormId] = useState<number | null>(null);
  const [noveltyLoading, setNoveltyLoading] = useState(false);
  const [noveltyResult, setNoveltyResult] = useState<NoveltyCheckResult | null>(null);
  const [noveltyQueries, setNoveltyQueries] = useState<NoveltyQuery[]>([]);
  const [noveltyQueryDialogOpen, setNoveltyQueryDialogOpen] = useState(false);
  const [noveltySelectedQuery, setNoveltySelectedQuery] = useState(1); // default to Moderate
  const [noveltyQueriesLoading, setNoveltyQueriesLoading] = useState(false);

  useEffect(() => {
    fetchForms();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await api.get('/forms');
      const formsData = response.data.forms;
      setForms(formsData);

      // Calculate stats
      setStats({
        total: formsData.length,
        draft: formsData.filter((f: Form) => f.status === 'draft').length,
        inProgress: formsData.filter((f: Form) => f.status === 'in_progress').length,
        submitted: formsData.filter((f: Form) =>
          ['submitted', 'under_review', 'approved'].includes(f.status)
        ).length,
      });
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewForm = async () => {
    if (creatingForm) return;

    setCreatingForm(true);
    try {
      const response = await api.post('/forms');
      const newForm = response.data.form;
      navigate(`/form/${newForm.id}`);
    } catch (error: any) {
      console.error('Failed to create form:', error);
      alert(error.response?.data?.error || 'Failed to create invention disclosure');
    } finally {
      setCreatingForm(false);
    }
  };

  const deleteForm = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this form?')) {
      try {
        await api.delete(`/forms/${id}`);
        fetchForms();
      } catch (error: any) {
        alert(error.response?.data?.error || 'Failed to delete form');
      }
    }
  };

  const downloadPDF = async (id: number, formNumber: string) => {
    try {
      const response = await api.get(`/forms/${id}/pdf`, {
        responseType: 'blob',
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `IDF-${formNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to download PDF');
    }
  };

  const handleAIClick = (formId: number) => {
    setSelectedFormId(formId);
    setPrivacyDialogOpen(true);
  };

  const handlePrivacyAccept = async () => {
    setPrivacyDialogOpen(false);
    setAiLoading(true);

    try {
      const response = await api.post(`/forms/${selectedFormId}/ai-assessment`);
      setAiAssessment(response.data.assessment);
      setResultsDialogOpen(true);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to generate AI assessment');
    } finally {
      setAiLoading(false);
    }
  };

  const handlePrivacyDecline = () => {
    setPrivacyDialogOpen(false);
    setSelectedFormId(null);
  };

  const handleResultsClose = () => {
    setResultsDialogOpen(false);
    setAiAssessment('');
    setSelectedFormId(null);
  };

  // Novelty Check handlers
  const handleNoveltyClick = (formId: number) => {
    setNoveltySelectedFormId(formId);
    setNoveltyPrivacyDialogOpen(true);
  };

  const handleNoveltyPrivacyAccept = async () => {
    setNoveltyPrivacyDialogOpen(false);
    setNoveltyQueriesLoading(true);

    try {
      const response = await api.post(`/forms/${noveltySelectedFormId}/novelty-queries`);
      setNoveltyQueries(response.data.queries);
      setNoveltySelectedQuery(1); // default to Moderate
      setNoveltyQueryDialogOpen(true);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to generate search queries');
    } finally {
      setNoveltyQueriesLoading(false);
    }
  };

  const handleNoveltyPrivacyDecline = () => {
    setNoveltyPrivacyDialogOpen(false);
    setNoveltySelectedFormId(null);
  };

  const handleNoveltyQueryDialogClose = () => {
    setNoveltyQueryDialogOpen(false);
    setNoveltyQueries([]);
    setNoveltySelectedFormId(null);
  };

  const handleNoveltyQuerySelect = async () => {
    const selectedQuery = noveltyQueries[noveltySelectedQuery];
    if (!selectedQuery) return;

    setNoveltyQueryDialogOpen(false);
    setNoveltyLoading(true);

    try {
      const response = await api.post(`/forms/${noveltySelectedFormId}/novelty-check`, {
        cqlQuery: selectedQuery.cqlQuery,
      });
      setNoveltyResult({
        cqlQuery: response.data.cqlQuery,
        analysis: response.data.analysis,
        patents: response.data.patents,
      });
      setNoveltyResultsDialogOpen(true);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to perform novelty check');
    } finally {
      setNoveltyLoading(false);
    }
  };

  const handleNoveltyResultsBack = () => {
    setNoveltyResultsDialogOpen(false);
    setNoveltyResult(null);
    setNoveltyQueryDialogOpen(true);
  };

  const handleNoveltyResultsClose = () => {
    setNoveltyResultsDialogOpen(false);
    setNoveltyResult(null);
    setNoveltyQueries([]);
    setNoveltySelectedFormId(null);
  };

  const formatPatentDate = (dateStr: string): string => {
    if (!dateStr || dateStr.length !== 8) return dateStr || 'N/A';
    return `${dateStr.slice(6, 8)}-${dateStr.slice(4, 6)}-${dateStr.slice(0, 4)}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'default';
      case 'in_progress': return 'warning';
      case 'submitted': return 'info';
      case 'under_review': return 'secondary';
      case 'approved': return 'success';
      default: return 'default';
    }
  };

  // Shared markdown renderer for AI Assessment and Novelty Check
  const renderFormattedText = (text: string) => {
    const elements: any[] = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
      const boldMatch = remaining.match(/^(.*?)\*\*(.*?)\*\*/);
      if (boldMatch) {
        if (boldMatch[1]) elements.push(boldMatch[1]);
        elements.push(<Box component="strong" key={key++} sx={{ fontWeight: 700 }}>{boldMatch[2]}</Box>);
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      const italicMatch = remaining.match(/^(.*?)\*(.*?)\*/);
      if (italicMatch) {
        if (italicMatch[1]) elements.push(italicMatch[1]);
        elements.push(<Box component="em" key={key++} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{italicMatch[2]}</Box>);
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }

      elements.push(remaining);
      break;
    }

    return elements;
  };

  const renderMarkdownContent = (text: string) => (
    <Box sx={{
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      lineHeight: 1.8,
      color: 'text.primary',
    }}>
      {text.split('\n').map((line, index) => {
        if (/^1\.\s+Overall Score:/.test(line)) {
          return (
            <Typography key={index} variant="h4" sx={{ fontWeight: 700, mb: 3, mt: 3, pb: 2, borderBottom: '2px solid', borderColor: 'divider' }}>
              {renderFormattedText(line)}
            </Typography>
          );
        } else if (/^\d+\.\s+[A-Z][^:]*:/.test(line)) {
          return (
            <Typography key={index} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 2, color: 'primary.main' }}>
              {renderFormattedText(line)}
            </Typography>
          );
        } else if (/^[A-Z][a-zA-Z\s]+:/.test(line) && !line.includes('*')) {
          return (
            <Typography key={index} variant="h6" sx={{ fontWeight: 600, mt: 2, mb: 1, color: 'text.primary' }}>
              {renderFormattedText(line)}
            </Typography>
          );
        } else if (/^\d+\.\s/.test(line) && !/^1\.\s+Overall Score:/.test(line)) {
          const lineText = line.replace(/^\d+\.\s/, '');
          const number = line.match(/^\d+/)?.[0];
          return (
            <Typography key={index} component="div" sx={{ mb: 1.5, pl: 3, fontSize: '1rem', lineHeight: 1.8 }}>
              <Box component="span" sx={{ fontWeight: 600, mr: 1 }}>{number}.</Box>
              {renderFormattedText(lineText)}
            </Typography>
          );
        } else if (/^\s*(?:\*|-|•)\s*$/.test(line)) {
          return null;
        } else if (/^\s*(?:\*|-|•)\s+/.test(line)) {
          const indent = (line.match(/^\s*/)?.[0].length || 0) / 4;
          const lineText = line.replace(/^\s*(?:\*|-|•)\s+/, '');
          return (
            <Typography key={index} component="div" sx={{ mb: 1.5, pl: 3 + indent * 3, display: 'flex', gap: 1, fontSize: '1rem', lineHeight: 1.8 }}>
              <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>•</Box>
              <Box sx={{ flex: 1 }}>{renderFormattedText(lineText)}</Box>
            </Typography>
          );
        } else if (/^#{1,6}\s/.test(line)) {
          const level = (line.match(/^#+/)?.[0].length || 1);
          const lineText = line.replace(/^#+\s/, '');
          const variant = level === 1 ? 'h4' : level === 2 ? 'h5' : level === 3 ? 'h6' : 'subtitle1';
          return (
            <Typography key={index} variant={variant} sx={{ fontWeight: 700, mt: level <= 3 ? 3 : 2.5, mb: 1.5, color: level === 1 ? 'primary.main' : 'text.primary' }}>
              {renderFormattedText(lineText)}
            </Typography>
          );
        } else if (/^---+$/.test(line.trim())) {
          return <Box key={index} sx={{ my: 3, borderTop: '1px solid', borderColor: 'divider' }} />;
        } else if (/\*\*.*\*\*|\*.*\*/.test(line)) {
          return (
            <Typography key={index} component="div" sx={{ mb: 1.5, fontSize: '1rem', lineHeight: 1.8 }}>
              {renderFormattedText(line)}
            </Typography>
          );
        } else if (line.trim()) {
          return (
            <Typography key={index} component="div" sx={{ mb: 1.5, fontSize: '1rem', lineHeight: 1.8, color: 'text.primary' }}>
              {line}
            </Typography>
          );
        } else {
          return <Box key={index} sx={{ height: '0.75rem' }} />;
        }
      })}
    </Box>
  );

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft': return <DescriptionIcon />;
      case 'in_progress': return <PendingIcon />;
      case 'submitted':
      case 'under_review':
      case 'approved': return <CheckIcon />;
      default: return <DescriptionIcon />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: 2, mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            IDF Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Welcome, {user?.name || user?.email}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={logout}
            size={isMobile ? 'small' : 'medium'}
          >
            Logout
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            endIcon={creatingForm ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={createNewForm}
            disabled={creatingForm}
            size={isMobile ? 'small' : 'medium'}
          >
            {creatingForm ? 'Creating...' : isMobile ? 'New Disclosure' : 'New Invention Disclosure'}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5">{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Forms
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'grey.500', mr: 2 }}>
                  <DescriptionIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5">{stats.draft}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Drafts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <PendingIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5">{stats.inProgress}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Progress
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                  <CheckIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5">{stats.submitted}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Submitted
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Forms Table */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Your Invention Disclosures
        </Typography>

        {loading ? (
          <LinearProgress />
        ) : forms.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              No invention disclosures found
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              endIcon={creatingForm ? <CircularProgress size={16} color="inherit" /> : null}
              onClick={createNewForm}
              disabled={creatingForm}
              sx={{ mt: 2 }}
            >
              {creatingForm ? 'Creating...' : 'Create Your First Disclosure'}
            </Button>
          </Box>
        ) : isMobile ? (
          /* Mobile: Card layout */
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {forms.map((form) => (
              <Card key={form.id} variant="outlined">
                <CardContent sx={{ pb: 1, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {form.title || <em>Untitled</em>}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {form.formNumber} &middot; {formatDateIST(form.updatedAt)}
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        form.status === 'submitted' || form.status === 'approved' || form.status === 'under_review'
                          ? '100%'
                          : `${form.completionPercentage}%`
                      }
                      color={
                        (form.status === 'submitted' || form.status === 'approved' || form.status === 'under_review')
                          ? 'success'
                          : form.completionPercentage >= 100
                            ? 'success'
                            : 'warning'
                      }
                      size="small"
                      sx={{ ml: 1, flexShrink: 0, minWidth: 64 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
                    {form.status === 'submitted' || form.status === 'approved' ? (
                      <>
                        <Button size="small" variant="outlined" startIcon={<ViewIcon />} onClick={() => navigate(`/form/${form.id}`)}>
                          View
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadPDF(form.id, form.formNumber)}>
                          PDF
                        </Button>
                        <Button size="small" variant="outlined" color="secondary" startIcon={<AutoAwesomeIcon />} onClick={() => handleAIClick(form.id)}>
                          AI Review
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={() => handleNoveltyClick(form.id)}>
                          Novelty
                        </Button>
                      </>
                    ) : (
                      <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/form/${form.id}`)}>
                        Edit
                      </Button>
                    )}
                    {form.status === 'draft' && (
                      <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => deleteForm(form.id)}>
                        Delete
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          /* Desktop: Table layout */
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Form Number</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {forms.map((form) => (
                  <TableRow key={form.id}>
                    <TableCell>{form.formNumber}</TableCell>
                    <TableCell>
                      {form.title || <em>Untitled</em>}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          form.status === 'submitted' || form.status === 'approved' || form.status === 'under_review'
                            ? '100%'
                            : `${form.completionPercentage}%`
                        }
                        color={
                          (form.status === 'submitted' || form.status === 'approved' || form.status === 'under_review')
                            ? 'success'
                            : form.completionPercentage >= 100
                              ? 'success'
                              : 'warning'
                        }
                        size="small"
                        sx={{ minWidth: 64 }}
                      />
                    </TableCell>
                    <TableCell>
                      {formatDateIST(form.updatedAt)}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end' }}>
                        {form.status === 'submitted' || form.status === 'approved' ? (
                          <>
                            <Button size="small" variant="outlined" startIcon={<ViewIcon />} onClick={() => navigate(`/form/${form.id}`)}>
                              View
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadPDF(form.id, form.formNumber)}>
                              PDF
                            </Button>
                            <Button size="small" variant="outlined" color="secondary" startIcon={<AutoAwesomeIcon />} onClick={() => handleAIClick(form.id)}>
                              AI Review
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<SearchIcon />} onClick={() => handleNoveltyClick(form.id)}>
                              Novelty
                            </Button>
                          </>
                        ) : (
                          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => navigate(`/form/${form.id}`)}>
                            Edit
                          </Button>
                        )}
                        {form.status === 'draft' && (
                          <Button size="small" variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => deleteForm(form.id)}>
                            Delete
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={creatingForm} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Creating Disclosure
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preparing your invention disclosure form...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Privacy Notice Dialog */}
      <Dialog
        open={privacyDialogOpen}
        onClose={handlePrivacyDecline}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Privacy Notice</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to submit your invention data to an AI service. Your data is treated with enterprise grade privacy and security. It will NOT be used for model training, fine tuning or ads targeting. Please proceed only if the invention is not sensitive. Otherwise, close this modal to decline an AI review of your invention disclosure.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePrivacyDecline} color="inherit">
            Decline
          </Button>
          <Button onClick={handlePrivacyAccept} variant="contained" color="primary">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Loading Dialog */}
      <Dialog open={aiLoading} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Analyzing Invention Disclosure
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while our AI examines your form...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* AI Results Dialog */}
      <Dialog
        open={resultsDialogOpen}
        onClose={handleResultsClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          AI Patent Assessment
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4 }}>
          {renderMarkdownContent(aiAssessment)}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              ⚠️ AI can make mistakes. Please use discretion.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResultsClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Novelty Check Privacy Dialog */}
      <Dialog
        open={noveltyPrivacyDialogOpen}
        onClose={handleNoveltyPrivacyDecline}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Privacy Notice - Novelty Check</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to submit your invention data to an AI service and the European Patent Office (EPO) patent database for a novelty search. Your data is treated with enterprise grade privacy and security. It will NOT be used for model training, fine tuning or ads targeting.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNoveltyPrivacyDecline} color="inherit">
            Decline
          </Button>
          <Button onClick={handleNoveltyPrivacyAccept} variant="contained" color="primary">
            Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Novelty Queries Loading Dialog */}
      <Dialog open={noveltyQueriesLoading} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Generating Search Queries
          </Typography>
          <Typography variant="body2" color="text.secondary">
            AI is crafting 3 patent search strategies for your invention...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Novelty Query Selection Dialog */}
      <Dialog
        open={noveltyQueryDialogOpen}
        onClose={handleNoveltyQueryDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Search Strategy</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Choose a search strategy for the EPO patent database. Broad finds more results, Tight finds fewer but more relevant ones.
          </Typography>
          <RadioGroup
            value={noveltySelectedQuery}
            onChange={(e) => setNoveltySelectedQuery(Number(e.target.value))}
          >
            {noveltyQueries.map((query, idx) => (
              <Card
                key={idx}
                variant="outlined"
                sx={{
                  mb: 2,
                  cursor: 'pointer',
                  borderColor: noveltySelectedQuery === idx ? 'primary.main' : 'divider',
                  borderWidth: noveltySelectedQuery === idx ? 2 : 1,
                  bgcolor: noveltySelectedQuery === idx ? 'action.selected' : 'background.paper',
                }}
                onClick={() => setNoveltySelectedQuery(idx)}
              >
                <CardContent sx={{ pb: '12px !important' }}>
                  <FormControlLabel
                    value={idx}
                    control={<Radio />}
                    label={
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {query.label}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {query.description}
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', mr: 0, width: '100%' }}
                  />
                  <Box sx={{
                    mt: 1,
                    ml: 4,
                    p: 1.5,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                    {query.cqlQuery}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNoveltyQueryDialogClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleNoveltyQuerySelect} variant="contained" color="primary" startIcon={<SearchIcon />}>
            Run Search
          </Button>
        </DialogActions>
      </Dialog>

      {/* Novelty Check Loading Dialog */}
      <Dialog open={noveltyLoading} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Running Novelty Check
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Searching patent databases and analyzing results. This may take up to 30 seconds...
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Novelty Check Results Dialog */}
      <Dialog
        open={noveltyResultsDialogOpen}
        onClose={handleNoveltyResultsClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Novelty Check Results
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4 }}>
          {noveltyResult && (
            <>
              {/* CQL Query Display */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  EPO Search Query (CQL)
                </Typography>
                <Box sx={{
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}>
                  {noveltyResult.cqlQuery}
                </Box>
              </Box>

              {/* AI Analysis */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  AI Novelty Analysis
                </Typography>
                {renderMarkdownContent(noveltyResult.analysis)}
              </Box>

              {/* Patent Cards */}
              {noveltyResult.patents.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Patents Found ({noveltyResult.patents.length})
                  </Typography>
                  {noveltyResult.patents.map((patent, idx) => (
                    <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                            {patent.title}
                          </Typography>
                          <Chip
                            label={patent.patentNumber}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1, flexShrink: 0 }}
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          {patent.abstract.length > 300
                            ? `${patent.abstract.slice(0, 300)}...`
                            : patent.abstract || 'No abstract available'}
                        </Typography>

                        {patent.ipcCodes.length > 0 && (
                          <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {patent.ipcCodes.map((code, cIdx) => (
                              <Chip key={cIdx} label={code} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                            ))}
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {patent.applicant && `${patent.applicant} | `}
                            {patent.publicationDate && `Published: ${formatPatentDate(patent.publicationDate)}`}
                          </Typography>
                          <Link
                            href={patent.espacenetUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}
                          >
                            View on Espacenet <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                          </Link>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {/* Disclaimer */}
              <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  This novelty check is based on a limited search of the EPO patent database and AI analysis. It is not a substitute for a comprehensive patent search or professional legal advice. Results may not cover all relevant prior art across all patent offices worldwide.
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {noveltyResult && noveltyResult.patents.length === 0 && noveltyQueries.length > 0 && (
            <Button onClick={handleNoveltyResultsBack} color="inherit">
              Back to Query Selection
            </Button>
          )}
          <Button onClick={handleNoveltyResultsClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          mb: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary" align="center">
          Turnip Innovations Private Limited © 2018-{new Date().getFullYear()}
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          Made with ❤️ in India.
        </Typography>
        <Box sx={{ mt: 2 }}>
          <img
            src="https://storage.googleapis.com/www.turnip.co.in/image/turnip_logo_transparent.png"
            alt="Turnip Innovations"
            style={{ maxWidth: '100px', height: 'auto' }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default Dashboard;
